import { apiClient } from './client'

export type ExerciseCategory =
  | 'Elevation'
  | 'Strength'
  | 'Balance'
  | 'Lunge'
  | 'HIIT'
  | 'Cardio'
  | 'Flexibility'
  | 'Recovery'

export type PackUsage = 'NoPack' | 'Optional' | 'Required'

export type ExerciseLocation = 'Inside' | 'Outside' | 'Either'
export type ExerciseSidedness = 'Sided' | 'NonSided'

export const PACK_USAGE_OPTIONS: { value: PackUsage; label: string }[] = [
  { value: 'NoPack', label: 'No Pack' },
  { value: 'Optional', label: 'Optional' },
  { value: 'Required', label: 'Required' }
]

export const EXERCISE_LOCATIONS: { value: ExerciseLocation; label: string }[] = [
  { value: 'Either', label: 'Either' },
  { value: 'Inside', label: 'Inside' },
  { value: 'Outside', label: 'Outside' }
]

export const EXERCISE_SIDEDNESS_OPTIONS: { value: ExerciseSidedness; label: string }[] = [
  { value: 'Sided', label: 'Left / Right' },
  { value: 'NonSided', label: 'Non-sided' }
]

export type Exercise = {
  id: string
  code: string
  name: string
  category: ExerciseCategory
  isTrueElevation: boolean
  isArtificialElevation: boolean
  contributesToDistance: boolean
  packUsage: PackUsage
  isKneeFriendly: boolean
  exerciseLocation: ExerciseLocation
  sidedness: ExerciseSidedness
  videoKey?: string | null
  secondaryVideoKey?: string | null
  thumbnailKey?: string | null
  exerciseTrackerInstructionKey?: string | null
  shortDescription?: string | null
  safetyNotes?: string | null
  caloriesPerMinute?: number | null
  isActive: boolean
  createdAt: string
  updatedAt?: string | null
}

export type ExercisePayload = {
  code: string
  name: string
  category: ExerciseCategory
  isTrueElevation: boolean
  isArtificialElevation: boolean
  contributesToDistance: boolean
  packUsage: PackUsage
  isKneeFriendly: boolean
  exerciseLocation: ExerciseLocation
  sidedness: ExerciseSidedness
  videoKey?: string | null
  secondaryVideoKey?: string | null
  thumbnailKey?: string | null
  exerciseTrackerInstructionKey?: string | null
  shortDescription?: string | null
  safetyNotes?: string | null
  caloriesPerMinute?: number | null
  isActive: boolean
}

export async function getExercises(): Promise<Exercise[]> {
  const { data } = await apiClient.get<Exercise[]>('/api/admin/exercises')
  return data
}

export async function getExerciseById(id: string): Promise<Exercise> {
  const { data } = await apiClient.get<Exercise>(`/api/admin/exercises/${id}`)
  return data
}

export async function createExercise(payload: ExercisePayload): Promise<Exercise> {
  const { data } = await apiClient.post<Exercise>('/api/admin/exercises', payload)
  return data
}

export async function updateExercise(id: string, payload: ExercisePayload): Promise<Exercise> {
  const { data } = await apiClient.put<Exercise>(`/api/admin/exercises/${id}`, payload)
  return data
}

export async function deleteExercise(id: string): Promise<void> {
  await apiClient.delete(`/api/admin/exercises/${id}`)
}

export async function deleteAllExercises(): Promise<{ deletedCount: number }> {
  const { data } = await apiClient.delete<{ message: string; deletedCount: number }>(
    '/api/admin/exercises/all'
  )
  return data
}

export const EXERCISE_CATEGORIES: ExerciseCategory[] = [
  'Elevation',
  'Strength',
  'Balance',
  'Lunge',
  'HIIT',
  'Cardio',
  'Flexibility',
  'Recovery'
]
